import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import html2pdf from 'html2pdf.js';
import InvoiceDisplay from '../components/InvoiceDisplay';
import { secureLog, safeErrorLog } from '../lib/secureLogger';

interface ReceiptToken {
  payment_id: string;
  business_id: string;
  booking_id?: string; // Optional for backward compatibility
  token: string;
  expires_at: string;
  created_at: string;
}

interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
}

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
}

interface Booking {
  booking_number: number;
  title: string;
  total_amount: number;
  customers: Customer;
}

interface PaymentData {
  id: string;
  booking_id: string;
  business_id: string;
  amount: number;
  method: string;
  status: string;
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  bookings: Booking;
}

interface Payment {
  id: string;
  booking_id: string;
  business_id: string;
  amount: number;
  method: string;
  status: string;
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  booking_number?: number;
  booking_title?: string;
  booking_total?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
}



export default function PublicReceipt() {
  const { id: token } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  
  const fetchReceiptData = async () => {
    try {
      if (!token) {
        setError('No receipt token provided');
        setLoading(false);
        return;
      }

      if (!supabase) {
        setError('Database connection is not available');
        setLoading(false);
        return;
      }

      // Normalize token for mobile compatibility - trim whitespace and force lowercase
      const normalizedToken = token.trim().toLowerCase();
      console.log('🔍 Looking up token:', { original: token, normalized: normalizedToken });

      // Get the token data - use case-insensitive comparison for mobile compatibility
      const { data: initialTokenData, error: tokenError } = await supabase
        .from('public_receipt_tokens')
        .select('*')
        .eq('token', normalizedToken)
        .single();

      let tokenData = initialTokenData;

      if (tokenError || !tokenData) {
        console.log('❌ Token lookup failed:', { tokenError, normalizedToken });
        // Try original token as fallback for backward compatibility
        const { data: fallbackTokenData, error: fallbackError } = await supabase
          .from('public_receipt_tokens')
          .select('*')
          .eq('token', token)
          .single();
          
        if (fallbackError || !fallbackTokenData) {
          safeErrorLog('Error fetching token data', 'PublicReceipt');
          setError('Invalid receipt token');
          setLoading(false);
          return;
        }
        
        // Use fallback data if found
        console.log('✅ Found token using fallback lookup');
        tokenData = fallbackTokenData;
      }

      // Check if token is expired
      if (new Date(tokenData.expires_at) < new Date()) {
        setError('Receipt link has expired');
        setLoading(false);
        return;
      }

      let transformedPayment: Payment;
      let bookingId: string;

      // Use booking_id directly from token data if available, otherwise fall back to old logic
      if (tokenData.booking_id) {
        bookingId = tokenData.booking_id;
      } else {
        // Fallback for old tokens without booking_id
        const PENDING_PAYMENT_UUID = '00000000-0000-0000-0000-000000000000';
        if (tokenData.payment_id === 'pending' || tokenData.payment_id === null || tokenData.payment_id === PENDING_PAYMENT_UUID) {
          // Get any booking for this business (old behavior)
          const { data: bookingData, error: bookingError } = await supabase
            .from('bookings')
            .select('id')
            .eq('business_id', tokenData.business_id)
            .single();

          if (bookingError || !bookingData) {
            safeErrorLog('Error fetching booking details', 'PublicReceipt');
            setError('Booking details not found');
            setLoading(false);
            return;
          }
          bookingId = bookingData.id;
        } else {
          // Get booking_id from payment
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
            .select('booking_id')
            .eq('id', tokenData.payment_id)
            .single();

          if (paymentError || !paymentData) {
            safeErrorLog('Error fetching payment booking ID', 'PublicReceipt');
            setError('Payment details not found');
            setLoading(false);
            return;
          }
          bookingId = paymentData.booking_id;
        }
      }

      // Get booking details for both pending and completed payments
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_number,
          title,
          total_amount,
          created_at,
          business_id,
          customer_id,
          customers!inner (
            first_name,
            last_name,
            email
          )
        `)
        .eq('id', bookingId)
        .single();

      if (bookingError || !bookingData) {
        safeErrorLog('Error fetching booking details', 'PublicReceipt');
        setError('Booking details not found');
        setLoading(false);
        return;
      }

      // Handle pending vs completed payments  
      const PENDING_PAYMENT_UUID = '00000000-0000-0000-0000-000000000000';
      if (tokenData.payment_id === 'pending' || tokenData.payment_id === null || tokenData.payment_id === PENDING_PAYMENT_UUID) {
        // Create a payment object for pending invoice
        transformedPayment = {
          id: 'pending',
          booking_id: bookingData.id,
          business_id: bookingData.business_id,
          amount: bookingData.total_amount,
          method: 'unpaid',
          status: 'pending',
          reference: null,
          notes: null,
          created_at: bookingData.created_at, // When the booking/invoice was created
          updated_at: bookingData.created_at,
          booking_number: bookingData.booking_number,
          booking_title: bookingData.title,
          booking_total: bookingData.total_amount,
          customer_first_name: (bookingData.customers as any)?.first_name,
          customer_last_name: (bookingData.customers as any)?.last_name,
          customer_email: (bookingData.customers as any)?.email,
          booking_created_at: bookingData.created_at, // Store both dates
          payment_created_at: null // No payment date for pending
        } as any;
      } else {
        // For actual payments, get payment details
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select(`
          id,
          booking_id,
          business_id,
          amount,
          method,
          status,
          reference,
          notes,
          created_at,
            updated_at
        `)
        .eq('id', tokenData.payment_id)
          .single();

      if (paymentError || !paymentData) {
        safeErrorLog('Error fetching payment details', 'PublicReceipt');
        setError('Payment details not found');
        setLoading(false);
        return;
      }

      // Transform the data to match our Payment interface
        transformedPayment = {
        ...paymentData,
          booking_number: bookingData.booking_number,
          booking_title: bookingData.title,
          booking_total: bookingData.total_amount,
          customer_first_name: (bookingData.customers as any)?.first_name,
          customer_last_name: (bookingData.customers as any)?.last_name,
          customer_email: (bookingData.customers as any)?.email,
          booking_created_at: bookingData.created_at, // When the booking was created
          payment_created_at: paymentData.created_at // When the payment was processed
        } as any;
      }

      // Now fetch detailed booking services and discounts
      secureLog.log('🔍 PublicReceipt Debug - Starting service fetch for booking ID:', bookingId);
      
      // First, let's check if the booking_services table has any data at all
      secureLog.log('🧪 Testing booking_services table access...');
      const { data: testData, error: testError } = await supabase
        .from('booking_services')
        .select('id, booking_id, name')
        .limit(5);
      
      secureLog.log('🧪 Test query results:');
      secureLog.log('- Test data:', testData);
      secureLog.log('- Test error:', testError);
      secureLog.log('- Total test records found:', testData?.length || 0);
      
             if (testData && testData.length > 0) {
         secureLog.log('✅ booking_services table has data');
         secureLog.log('- Sample booking IDs in table:', testData.map(d => d.booking_id));
         
         // Check if our specific booking ID exists in the services table
         const hasOurBooking = testData.some(d => d.booking_id === bookingId);
         secureLog.log(`- Our booking ID ${bookingId} found in services table:`, hasOurBooking);
       } else {
         secureLog.log('⚠️ booking_services table appears empty or inaccessible');
       }
       
       // Also verify the booking exists in the bookings table
       secureLog.log('🧪 Verifying booking exists in bookings table...');
       const { data: bookingExists, error: bookingExistsError } = await supabase
         .from('bookings')
         .select('id, booking_number, title')
         .eq('id', bookingId)
         .single();
       
       secureLog.log('🧪 Booking verification:');
       secureLog.log('- Booking exists data:', bookingExists);
       secureLog.log('- Booking exists error:', bookingExistsError);
       
       if (bookingExists) {
         secureLog.log('✅ Booking found in bookings table');
         secureLog.log(`- Booking: #${bookingExists.booking_number} - ${bookingExists.title}`);
       } else {
         secureLog.log('❌ Booking NOT found in bookings table');
       }
      
              const { data: servicesData, error: servicesError } = await supabase
        .from('booking_services')
        .select(`
          *,
          customer_vehicles (
            make,
            model,
            year,
            color,
            license_plate
          )
        `)
        .eq('booking_id', bookingId);

      secureLog.log('📊 Services Query Result:');
      secureLog.log('- Services data:', servicesData);
      secureLog.log('- Services error:', servicesError);
      secureLog.log('- Services count:', servicesData?.length || 0);

      // Fetch add-ons separately if services exist
      let addonsData: any[] = [];
      if (servicesData && servicesData.length > 0) {
        secureLog.log('🔧 Fetching add-ons for services:', servicesData.map(s => s.id));
        
        const serviceIds = servicesData.map(service => service.id);
        const { data: addons, error: addonsError } = await supabase
          .from('booking_service_addons')
          .select(`
            id,
            name,
            description,
            price,
            duration_minutes,
            booking_service_id
          `)
          .in('booking_service_id', serviceIds);
        
        secureLog.log('🔧 Add-ons Query Result:');
        secureLog.log('- Add-ons data:', addons);
        secureLog.log('- Add-ons error:', addonsError);
        secureLog.log('- Add-ons count:', addons?.length || 0);
        
        if (!addonsError && addons) {
          addonsData = addons;
        }
      } else {
        secureLog.log('⚠️ No services found, skipping add-ons fetch');
      }

      // Fetch booking discounts
      secureLog.log('💰 Fetching discounts for booking ID:', bookingId);
      
      const { data: discountsData, error: discountsError } = await supabase
        .from('booking_discounts')
        .select(`
          id,
          title,
          type,
          amount
        `)
        .eq('booking_id', bookingId);

      secureLog.log('💰 Discounts Query Result:');
      secureLog.log('- Discounts data:', discountsData);
      secureLog.log('- Discounts error:', discountsError);
      secureLog.log('- Discounts count:', discountsData?.length || 0);

      // Debug logging to see what's happening
      secureLog.log('🔍 PublicReceipt Debug Summary:');
      secureLog.log('- Token data:', tokenData);
      secureLog.log('- Booking ID used:', bookingId);
      secureLog.log('- Booking data:', bookingData);
      secureLog.log('- Services found:', servicesData?.length || 0);
      secureLog.log('- Add-ons found:', addonsData?.length || 0);
      secureLog.log('- Discounts found:', discountsData?.length || 0);
      secureLog.log('- Payment before services attachment:', transformedPayment);

      // Add services and discounts to the payment object
      secureLog.log('🔗 Attaching services to payment object...');
      
      if (servicesData && !servicesError && servicesData.length > 0) {
        secureLog.log('✅ Services data is valid, transforming...');
        
        // Transform services and attach their add-ons
        const transformedServices = servicesData.map(service => {
          const serviceAddons = addonsData.filter(addon => addon.booking_service_id === service.id) || [];
          secureLog.log(`🔗 Service "${service.name}" has ${serviceAddons.length} add-ons:`, serviceAddons);
          
          // Get vehicle data from the relationship or stored fields
          const vehicleData = service.customer_vehicles;
          secureLog.log(`🚗 Vehicle relationship data for "${service.name}":`, vehicleData);
          secureLog.log(`🚗 Stored vehicle data for "${service.name}":`, {
            vehicle_make: service.vehicle_make,
            vehicle_model: service.vehicle_model, 
            vehicle_name: service.vehicle_name
          });
          
          // Use actual vehicle data if available, otherwise use stored data, then fallback
          let vehicleMake, vehicleModel, vehicleName;
          
          if (vehicleData) {
            // Use data from customer_vehicles table
            vehicleMake = vehicleData.make || 'Customer';
            vehicleModel = vehicleData.model || 'Vehicle';
            vehicleName = vehicleData.year ? 
              `${vehicleData.year} ${vehicleData.make || ''} ${vehicleData.model || ''}`.trim() :
              `${vehicleData.make || 'Customer'} ${vehicleData.model || 'Vehicle'}`.trim();
          } else {
            // Use stored data or fallback
            vehicleMake = (service.vehicle_make && service.vehicle_make.trim() !== '') ? service.vehicle_make : 'Customer';
            vehicleModel = (service.vehicle_model && service.vehicle_model.trim() !== '') ? service.vehicle_model : 'Vehicle';
            vehicleName = (service.vehicle_name && service.vehicle_name.trim() !== '') ? service.vehicle_name : 'Customer Vehicle';
          }
          
          const transformedService = {
            ...service,
            vehicle_make: vehicleMake,
            vehicle_model: vehicleModel,
            vehicle_name: vehicleName,
            add_ons: serviceAddons
          };
          
          secureLog.log(`🚗 Final vehicle data for "${service.name}":`, {
            vehicle_make: transformedService.vehicle_make,
            vehicle_model: transformedService.vehicle_model,
            vehicle_name: transformedService.vehicle_name
          });
          
          return transformedService;
        });
        
        secureLog.log('✅ Transformed services with add-ons:', transformedServices);
        
        // Attach to payment object
        (transformedPayment as any).booking_services = transformedServices;
        (transformedPayment as any).booking_subtotal = transformedServices.reduce((sum, service) => sum + service.price, 0);
        
                 secureLog.log('✅ Services attached to payment object');
         secureLog.log('- booking_services length:', (transformedPayment as any).booking_services?.length);
         secureLog.log('- booking_subtotal:', (transformedPayment as any).booking_subtotal);
      } else {
        secureLog.log('❌ No services data found or error occurred');
        secureLog.log('- servicesData:', servicesData);
        secureLog.log('- servicesError:', servicesError);
        secureLog.log('- Will create fallback service data');
        
        // Create fallback service data based on booking info
        const fallbackService = {
          id: 'fallback-' + bookingData.id,
          name: bookingData.title || 'Automotive Service',
          description: 'Professional automotive service',
          price: transformedPayment.amount / 1.05, // Remove tax to get base price
          tax_amount: transformedPayment.amount - (transformedPayment.amount / 1.05),
          total_amount: transformedPayment.amount,
          duration_minutes: 60,
          vehicle_make: 'Vehicle',
          vehicle_model: 'Details',
          vehicle_name: 'Customer Vehicle',
          tax_exempt: false,
          tax_rate: 0.05,
          add_ons: []
        };
        
        secureLog.log('- Created fallback service:', fallbackService);
        (transformedPayment as any).booking_services = [fallbackService];
        (transformedPayment as any).booking_subtotal = fallbackService.price;
      }

      if (discountsData && !discountsError) {
        secureLog.log('✅ Attaching discounts to payment object');
        (transformedPayment as any).booking_discounts = discountsData;
      } else {
        secureLog.log('⚠️ No discounts found or error occurred');
        secureLog.log('- discountsData:', discountsData);
        secureLog.log('- discountsError:', discountsError);
      }

      secureLog.log('🎯 Final payment object with all data attached:');
      secureLog.log('- Payment ID:', transformedPayment.id);
      secureLog.log('- Booking ID:', transformedPayment.booking_id);
      secureLog.log('- Booking Title:', transformedPayment.booking_title);
             secureLog.log('- Services Count:', (transformedPayment as any).booking_services?.length || 0);
       secureLog.log('- Services Data:', (transformedPayment as any).booking_services);
      secureLog.log('- Customer:', `${transformedPayment.customer_first_name} ${transformedPayment.customer_last_name}`);
      secureLog.log('- Amount:', transformedPayment.amount);
      secureLog.log('- Full Payment Object:', transformedPayment);

      // Get business details
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', tokenData.business_id)
        .single();

      if (businessError) {
        safeErrorLog('Error fetching business details', 'PublicReceipt');
        setError('Error fetching business information');
        setLoading(false);
        return;
      }

      if (!businessData) {
        safeErrorLog('Business not found', 'PublicReceipt');
        setError('Business information not found');
        setLoading(false);
        return;
      }

      setPayment(transformedPayment);
      setBusiness(businessData);
      setLoading(false);
    } catch (err) {
      safeErrorLog('Error fetching receipt data', 'PublicReceipt');
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchReceiptData();
  }, [token]);
  
  const handleDownloadPdf = async () => {
    try {
      if (!invoiceRef.current) return;
      
      setIsDownloading(true);
      
      // Create a clone of the invoice element to modify for PDF
      const element = invoiceRef.current.cloneNode(true) as HTMLElement;
      
      // Remove any buttons or interactive elements from the PDF
      const buttonsToRemove = element.querySelectorAll('button');
      buttonsToRemove.forEach(button => button.remove());
      
      // Add a PDF-specific class for styling
      element.classList.add('pdf-invoice');
      
      // Create a temporary container with proper styling for PDF generation
      const container = document.createElement('div');
      container.appendChild(element);
      container.style.width = '210mm'; // A4 width
      container.style.padding = '10mm';
      container.style.backgroundColor = 'white';
      
      // Make sure stamps are visible in PDF
      const stamps = container.querySelectorAll('[class*="rotate"]');
      stamps.forEach(stamp => {
        const stampEl = stamp as HTMLElement;
        stampEl.style.position = 'absolute';
        stampEl.style.zIndex = '5';
        stampEl.style.opacity = '0.3';
      });
      
      // Ensure table layouts are preserved
      const tables = container.querySelectorAll('table');
      tables.forEach(table => {
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
      });
      
      // Add business information and invoice title on top
      const headerText = document.createElement('div');
      headerText.style.textAlign = 'center';
      headerText.style.marginBottom = '20px';
      headerText.innerHTML = `<h1 style="font-size: 24px; color: #333; margin-bottom: 5px;">INVOICE</h1>
                             <p style="margin: 0; color: #666;">${business?.name || 'Auto Service'}</p>`;
      container.insertBefore(headerText, container.firstChild);
      
      // Add footer with page number
      const footer = document.createElement('div');
      footer.style.textAlign = 'center';
      footer.style.marginTop = '20px';
      footer.style.fontSize = '10px';
      footer.style.color = '#999';
      footer.innerHTML = 'Thank you for your business';
      container.appendChild(footer);
      
      // Set options for the PDF
      const options = {
        margin: [5, 0, 5, 0],
        filename: `Invoice-${payment?.booking_number || 'Receipt'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' as 'portrait',
          compress: true
        }
      };
      
      // Generate and download the PDF
      await html2pdf().from(container).set(options).save();
      
    } catch (err) {
      safeErrorLog('Error generating PDF', 'PublicReceipt');
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }
  
  // Show error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md">
          <div className="text-center text-red-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Error</h2>
          </div>
          <p className="text-gray-700 text-center">{error}</p>
        </div>
      </div>
    );
  }
  
  // Show expired message
  if (expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md">
          <div className="text-center text-yellow-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Link Expired</h2>
          </div>
          <p className="text-gray-700 text-center">This receipt link has expired. Please contact the business for a new receipt.</p>
        </div>
      </div>
    );
  }
  
  if (!payment || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md">
          <div className="text-center text-yellow-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Receipt Not Found</h2>
          </div>
          <p className="text-gray-700 text-center">We couldn't find the receipt you're looking for. Please check the link and try again.</p>
        </div>
      </div>
    );
  }
  
  // Show receipt
  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {payment.status === 'pending' ? 'Invoice' : 'Receipt'}
              </h1>
              <p className="text-gray-600">#{payment.booking_number}</p>
            </div>
            <button 
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
            >
              <Download className="mr-2 h-4 w-4" />
              {isDownloading ? 'Downloading...' : `Download ${payment.status === 'pending' ? 'Invoice' : 'Receipt'}`}
            </button>
          </div>
          
          <InvoiceDisplay 
            payment={payment as any}
            business={business as any}
            forwardRef={invoiceRef}
          />
        </div>
      </div>
    </div>
  );
} 